import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Sparkles, MapPin } from "lucide-react";
import VerifiedBadge from "../components/VerifiedBadge";

const CATEGORIES = [
  { k: "", l: "All" },
  { k: "astrology", l: "🔮 Astrology" },
  { k: "aarti", l: "🪔 Aarti & Puja" },
  { k: "puja", l: "🙏 Rituals" },
  { k: "lecture", l: "📖 Discourses" },
  { k: "bhajan", l: "🎵 Bhajan Singers" },
  { k: "music", l: "🎼 Devotional Music" },
  { k: "wedding", l: "💒 Weddings" },
  { k: "household", l: "🏠 House Pujas" },
  { k: "event", l: "✨ Event Services" },
  { k: "vastu", l: "🏛️ Vastu" },
  { k: "tarot", l: "🃏 Tarot" },
];

export default function Teachers() {
  const [gurus, setGurus] = useState([]);
  const [cat, setCat] = useState("");

  useEffect(() => {
    api.get(`/gurus${cat ? `?category=${cat}` : ""}`).then((r) => setGurus(r.data.items));
  }, [cat]);

  return (
    <div data-testid="teachers-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="max-w-2xl">
          <div className="overline">॥ गुरव नमो नमः ॥</div>
          <h1 className="font-serif text-5xl lg:text-6xl text-[#4A0E17] mt-2 leading-tight">Gurus · Astrologers · Scholars</h1>
          <p className="text-[#63534E] mt-5 text-lg leading-relaxed">
            Book one-on-one aarti, puja, astrology reading, discourse or ritual guidance — directly from verified teachers. Every booking supports both the teacher and Pancham Dham.
          </p>
        </div>
        <Link data-testid="teachers-apply-cta" to="/teachers/apply" className="btn-saffron">
          <Sparkles className="w-4 h-4" /> Apply to teach
        </Link>
      </div>

      <div className="mt-10 flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <button key={c.k || "all"} data-testid={`cat-${c.k || "all"}`} onClick={() => setCat(c.k)}
            className={`px-4 h-10 rounded-full text-sm transition-sacred border ${cat === c.k ? "bg-[#4A0E17] text-[#F3E5AB] border-[#4A0E17]" : "bg-[#FFFDF9] text-[#4A0E17] border-[#D4AF37]/40"}`}>
            {c.l}
          </button>
        ))}
      </div>

      {gurus.length === 0 ? (
        <div className="mt-14 bg-[#FFFDF9] sacred-border rounded-3xl p-16 text-center">
          <div className="font-devanagari text-[80px] text-[#D4AF37] leading-none opacity-50">ॐ</div>
          <h3 className="font-serif text-2xl text-[#4A0E17] mt-4">The circle is forming</h3>
          <p className="text-[#63534E] mt-3 max-w-md mx-auto">We're welcoming our founding teachers now. Be the first to join and anchor the sangha.</p>
          <Link to="/teachers/apply" data-testid="empty-apply" className="btn-primary mt-6">Become a founding teacher</Link>
        </div>
      ) : (
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {gurus.map((g) => (
            <Link key={g.id} data-testid={`guru-${g.id}`} to={`/teachers/${g.slug || g.id}`}
              className="group bg-[#FFFDF9] sacred-border rounded-2xl overflow-hidden transition-sacred gold-glow block">
              <div className="h-48 bg-gradient-to-br from-[#4A0E17] to-[#36080F] flex items-center justify-center relative">
                {g.photo_url ? (
                  <img src={g.photo_url} alt={g.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="font-devanagari text-[80px] text-[#D4AF37] opacity-30">ॐ</div>
                )}
              </div>
              <div className="p-5">
                <h3 className="font-serif text-xl text-[#4A0E17] flex items-center gap-1.5">{g.name} <VerifiedBadge show={g.verified} /></h3>
                <div className="text-sm text-[#63534E] mt-1">{g.title}</div>
                <div className="flex flex-wrap gap-1 mt-3">
                  {(g.categories || []).slice(0, 3).map((c) => (
                    <span key={c} className="text-xs bg-[#F3E5AB]/50 border border-[#D4AF37]/30 rounded-full px-2 py-0.5 capitalize text-[#4A0E17]">{c}</span>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-4 text-xs text-[#63534E]">
                  {g.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {g.location}</span>}
                  <span className="font-mono text-[#E65100]">{g.service_count} service{g.service_count !== 1 ? "s" : ""}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
